import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { navigateToEnterpriseDemo } from "@/utils/navigation";

interface FinalCTAProps {
  // New props
  headline?: React.ReactNode;
  subheadline?: string;
  bodyText?: React.ReactNode;
  primaryButtonText?: string;
  primaryButtonLink?: string;
  secondaryButtonText?: string;
  secondaryButtonLink?: string;
  // Legacy props for backward compatibility
  title?: string;
  subtitle?: string;
  signupUrl?: string;
}

const FinalCTA = ({ 
  headline,
  subheadline,
  bodyText = "Fusion puts your vision at the centre, combining your strategy with AI to create marketing that stands out.\nThis is the platform where your ideas come to life and your brand leads the way.",
  primaryButtonText = "Book Your Fusion Session",
  // Legacy props
  title,
  subtitle
}: FinalCTAProps) => {
  // Use legacy props as fallbacks
  const displayHeadline = headline || title || "Ready to Make Marketing Unfair?";
  const displaySubheadline = subheadline || subtitle || "Let's map your version of Fusion";
  return (
    <section
      id="final-cta-section"
      className="py-24 sm:py-32 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden"
    >
      {/* Animated background elements */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/30 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-purple-500/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-cyan-500/20 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-6 relative z-10">
        <div className="max-w-4xl mx-auto text-center space-y-6 animate-fade-in">
          <h2
            id="final-cta-heading"
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight"
          >
            {typeof displayHeadline === 'string' && displayHeadline.includes("Unfair") ? (
              <>
                {displayHeadline.split("Unfair")[0]}
                <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400 bg-clip-text text-transparent">
                  Unfair
                </span>
                {displayHeadline.split("Unfair")[1]}
              </>
            ) : (
              displayHeadline
            )}
          </h2>

          <p className="text-xl sm:text-2xl md:text-3xl font-semibold text-white">
            {displaySubheadline}
          </p>

          <p className="text-base sm:text-lg text-slate-300 max-w-3xl mx-auto">
            {bodyText}
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-8">
            <Button
              id="final-cta-primary"
              size="lg"
              className="text-base sm:text-lg px-8 sm:px-10 py-6 bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500 hover:from-blue-600 hover:via-cyan-600 hover:to-purple-600 text-white font-semibold shadow-[0_0_40px_rgba(96,165,250,0.5)] hover:shadow-[0_0_60px_rgba(96,165,250,0.7)] border-0 group"
              onClick={() => navigateToEnterpriseDemo()}
            >
              {primaryButtonText}
              <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FinalCTA;
